import { Grid, Modal, Result, Skeleton } from 'antd'
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useParams } from 'react-router-dom'
import i18n from '../../lib/i18n'
import styles from './QuestionsLearningView.module.scss'
import { getQuestions } from './actions'
import { emptyDraft, isComplete, isCorrect, prepareOrder } from './grading'
import TopBar from './components/TopBar/TopBar'
import QuestionCard from './components/QuestionCard/QuestionCard'
import ActionBar from './components/ActionBar/ActionBar'
import Summary from './components/Summary/Summary'
import SideNav from './components/SideNav/SideNav'

const prepareOrders = (questions) => Object.fromEntries(questions.map(_q => [_q.id, prepareOrder(_q)]))

// Public page shared with learners: answer the questions of a category one by one
function QuestionsLearningView() {
  const { id } = useParams()
  const [loaded, setLoaded] = useState(false)
  const [category, setCategory] = useState(null)
  const [questions, setQuestions] = useState([])
  const [index, setIndex] = useState(0)
  const [drafts, setDrafts] = useState({})
  const [results, setResults] = useState({})
  const [orders, setOrders] = useState({})
  const [showSummary, setShowSummary] = useState(false)
  const screens = Grid.useBreakpoint()

  useEffect(() => {
    getQuestions(id).then((data) => {
      const list = data?.questions || []
      setQuestions(list)
      setCategory(data?.category || null)
      setOrders(prepareOrders(list))
      setLoaded(true)
    })
  }, [id])

  if (!loaded) {
    return (
      <div className={styles.QuestionsLearningView}>
        <div className={styles.layout}><div className={styles.skeleton}><Skeleton active paragraph={{ rows: 6 }} /></div></div>
      </div>
    )
  }

  if (questions.length === 0) {
    return (
      <div className={styles.QuestionsLearningView}>
        <Result status="404" title={i18n('qv_not_found_title')} subTitle={i18n('qv_not_found_text')} />
      </div>
    )
  }

  const question = questions[index]
  const draft = drafts[question.id] ?? emptyDraft(question)
  const result = results[question.id]
  const checked = result !== undefined
  const canCheck = !checked && isComplete(question, draft)
  const multiple = questions.length > 1
  // From tablet up the question list is a scrollable left column; on phones it is a chip row in TopBar
  const withNav = multiple && screens.md !== false
  const correctCount = questions.filter(_q => results[_q.id] === true).length
  const answeredCount = questions.filter(_q => results[_q.id] !== undefined).length

  const goTo = (next) => {
    setShowSummary(false)
    setIndex(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const onCheck = () => {
    if (!canCheck) return
    setResults({ ...results, [question.id]: isCorrect(question, draft) })
  }

  const onRetry = () => {
    const nextResults = { ...results }
    delete nextResults[question.id]
    setResults(nextResults)
    setDrafts({ ...drafts, [question.id]: emptyDraft(question) })
    setOrders({ ...orders, [question.id]: prepareOrder(question) })
  }

  const onNext = () => {
    if (index < questions.length - 1) goTo(index + 1)
    else {
      setShowSummary(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const restart = () => {
    setDrafts({})
    setResults({})
    setOrders(prepareOrders(questions))
    goTo(0)
  }

  const onReset = () => {
    Modal.confirm({
      autoFocusButton: null,
      title: i18n('qv_start_over_title'),
      content: i18n('qv_start_over_text'),
      okText: i18n('qv_start_over'),
      cancelText: i18n('qv_cancel'),
      onOk: restart,
    })
  }

  return (
    <div className={styles.QuestionsLearningView}>
      <TopBar
        title={category?.name}
        questions={questions}
        results={results}
        index={showSummary ? -1 : index}
        showNav={!withNav}
        onSelect={goTo}
        onReset={onReset}
      />

      <div className={classNames(styles.layout, { [styles.withNav]: withNav })}>
        {withNav && (
          <SideNav questions={questions} results={results} index={showSummary ? -1 : index} onSelect={goTo} />
        )}
        <main className={styles.main}>
          {showSummary ? (
            <Summary
              total={questions.length}
              correct={correctCount}
              answered={answeredCount}
              onReview={() => goTo(0)}
              onRestart={restart}
            />
          ) : (
            <QuestionCard
              key={question.id}
              question={question}
              number={index + 1}
              total={questions.length}
              draft={draft}
              order={orders[question.id] || []}
              result={result}
              onChange={(value) => setDrafts({ ...drafts, [question.id]: value })}
              onSubmit={onCheck}
            />
          )}
        </main>
      </div>

      {!showSummary && (
        <ActionBar
          withNav={withNav}
          checked={checked}
          result={result}
          canCheck={canCheck}
          multiple={multiple}
          hasPrev={index > 0}
          hasNext={index < questions.length - 1}
          onPrev={() => goTo(index - 1)}
          onNext={onNext}
          onCheck={onCheck}
          onRetry={onRetry}
        />
      )}
    </div>
  )
}

export default QuestionsLearningView
